import { RowCrudStatus } from "../../common/types";
import {
  Edition,
  EditionCreateMutationVariables,
  EditionUpdateMutationVariables,
} from "../../../api/graphql";
import { LangStore } from "../../../store";
import { editionDocumentationWorkflowStateDisplayId } from "../../editionDocumentationWorkflowState/store/EditionDocumentationWorkflowStateCommonStore";
import { diaDeCalendario } from "../../../common/calendarDate";
import { FileCRUDType, fileCRUDInit } from "../../common/types";
import { formatBytes } from "../../../common/utils";
import { editionTypeDisplayId } from "../../editionType/store/EditionTypeCommonStore";
import { EditionContentType } from "../../../api/graphql";
import { blankToNull } from "../../../common/blankToNull";
import { musicalWorkDisplayId } from "../../musicalWork/store/MusicalWorkCommonStore";
import {
  EditionDocumentationValuesType,
  getEditionDocumentationValues,
} from "../../editionDocumentation/store/EditionDocumentationCommonStore";
import {
  AssociatedDocumentEditionValuesType,
  getAssociatedDocumentEditionValues,
} from "../../associatedDocumentEdition/store/AssociatedDocumentEditionCommonStore";
import {
  UserAttachmentEditionValuesType,
  getUserAttachmentEditionValues,
  getUserAttachmentEditionCreateInput,
  getUserAttachmentEditionUpdateInput,
} from "../../userAttachmentEdition/store/UserAttachmentEditionCommonStore";
import {
  addedStatusList,
  originalDeletedStatusList,
  originalUpdatedStatusList,
} from "../../common/types";

let uniqueCounter = 1;
export type EditionValuesType = {
  id: string;
  displayId: string;
  version_: string;
  __status__: RowCrudStatus;
  documentationState_transitions_: string[];
  __documentationState: string;
  showDocumentationState: { displayId: string; id: string } | null;
  documentationLastOperationalError: string;
  publicationDate: string | null;
  ejemplarPdf: string;
  ejemplarPdf_data: FileCRUDType;
  __ejemplarPdf_files: FileCRUDType[];
  name: string;
  __editionType: string;
  showEditionType: { displayId: string; id: string } | null;
  contentType: EditionContentType;
  publicationLink: string;
  editionNumber: number;
  price: number;
  __works: string[];
  showWorks: { displayId: string; id: string }[];
  notes: string;
  dndaExpedienteNumber: string;
  contractObservation: string;
  showDocumentation: EditionDocumentationValuesType[];
  showAssociatedDocuments: AssociatedDocumentEditionValuesType[];
  showUserAttachments: UserAttachmentEditionValuesType[];
  createdAt_: Date | null;
  lastUpdatedAt_: Date | null;
};

export type EditionErrorsType = {
  documentationState?: { message: string; fieldLabel: string };
  documentationLastOperationalError?: { message: string; fieldLabel: string };
  publicationDate?: { message: string; fieldLabel: string };
  ejemplarPdf?: { message: string; fieldLabel: string };
  name?: { message: string; fieldLabel: string };
  editionType?: { message: string; fieldLabel: string };
  contentType?: { message: string; fieldLabel: string };
  publicationLink?: { message: string; fieldLabel: string };
  editionNumber?: { message: string; fieldLabel: string };
  price?: { message: string; fieldLabel: string };
  works?: { message: string; fieldLabel: string };
  notes?: { message: string; fieldLabel: string };
  dndaExpedienteNumber?: { message: string; fieldLabel: string };
  contractObservation?: { message: string; fieldLabel: string };
  documentation?: { message: string; fieldLabel: string };
  associatedDocuments?: { message: string; fieldLabel: string };
  userAttachments?: { message: string; fieldLabel: string };
};

export function EditionValuesInit(): EditionValuesType {
  return {
    id: "",
    displayId: "",
    version_: "",
    __status__: "empty",
    documentationState_transitions_: [],
    __documentationState: "",
    showDocumentationState: null,
    documentationLastOperationalError: "",
    publicationDate: null,
    ejemplarPdf: "",
    ejemplarPdf_data: fileCRUDInit(),
    __ejemplarPdf_files: [],
    name: "",
    __editionType: "",
    showEditionType: null,
    contentType: "scoresAndAudios" as EditionContentType,
    publicationLink: "",
    editionNumber: 0,
    price: 1,
    __works: [],
    showWorks: [],
    notes: "",
    dndaExpedienteNumber: "",
    contractObservation: "",
    showDocumentation: [],
    showAssociatedDocuments: [],
    showUserAttachments: [],
    createdAt_: null,
    lastUpdatedAt_: null,
  };
}

export type EditionType = Edition | EditionValuesType;

export function editionDisplayId(
  source: Partial<Edition>,
  _langStore: LangStore
) {
  return source.name ?? _langStore.emptyStringSymbol;
}

export function getEditionValues(
  source: EditionType,
  _langStore: LangStore,
  _duplicated: boolean = false
): EditionValuesType {
  const entityValues = EditionValuesInit();
  entityValues.id = source.id;
  entityValues.displayId =
    "__status__" in source
      ? source.displayId
      : editionDisplayId(source, _langStore);
  entityValues.version_ = source.version_;
  entityValues.documentationState_transitions_ =
    source.documentationState_transitions_;
  entityValues.showDocumentationState =
    "documentationState" in source && source.documentationState
      ? {
          id: source.documentationState.id,
          displayId: editionDocumentationWorkflowStateDisplayId(
            source.documentationState,
            _langStore
          ),
        }
      : "showDocumentationState" in source && source.showDocumentationState
      ? {
          id: source.showDocumentationState.id,
          displayId: source.showDocumentationState.displayId,
        }
      : null;
  entityValues.__documentationState =
    entityValues.showDocumentationState?.displayId || "";
  entityValues.documentationLastOperationalError =
    source.documentationLastOperationalError || "";
  entityValues.publicationDate = diaDeCalendario(source.publicationDate);
  entityValues.__ejemplarPdf_files =
    source.ejemplarPdf_data && !_duplicated
      ? [
          {
            id:
              "id" in source.ejemplarPdf_data && source.ejemplarPdf_data.id
                ? source.ejemplarPdf_data.id
                : `generated_${uniqueCounter++}`,
            name: source.ejemplarPdf_data.name,
            description: source.ejemplarPdf_data.description || undefined,
            size: source.ejemplarPdf_data.size || 0,
            formattedSize: source.ejemplarPdf_data.size
              ? formatBytes(source.ejemplarPdf_data.size)
              : "",
            mimetype: source.ejemplarPdf_data.mimetype,
            url: source.ejemplarPdf_data.url || undefined,
            __status__: "original",
          },
        ]
      : [];
  entityValues.ejemplarPdf =
    source.ejemplarPdf && !_duplicated ? source.ejemplarPdf : "";
  entityValues.ejemplarPdf_data =
    source.ejemplarPdf_data && "__status__" in source.ejemplarPdf_data
      ? source.ejemplarPdf_data
      : source.ejemplarPdf_data
      ? {
          id: ("id" in source.ejemplarPdf_data && source.ejemplarPdf_data.id
            ? source.ejemplarPdf_data.id
            : `generated_${uniqueCounter++}`) as string,
          name: source.ejemplarPdf_data.name,
          description: source.ejemplarPdf_data.description || undefined,
          size: source.ejemplarPdf_data.size || 0,
          formattedSize: source.ejemplarPdf_data.size
            ? formatBytes(source.ejemplarPdf_data.size)
            : "",
          mimetype: source.ejemplarPdf_data.mimetype,
          url: source.ejemplarPdf_data.url || undefined,
          __status__: "original" as RowCrudStatus,
        }
      : fileCRUDInit();
  entityValues.name = source.name;
  entityValues.showEditionType =
    "editionType" in source && source.editionType
      ? {
          id: source.editionType.id,
          displayId: editionTypeDisplayId(source.editionType, _langStore),
        }
      : "showEditionType" in source && source.showEditionType
      ? {
          id: source.showEditionType.id,
          displayId: source.showEditionType.displayId,
        }
      : null;
  entityValues.__editionType = entityValues.showEditionType?.displayId || "";
  entityValues.contentType = source.contentType;
  entityValues.publicationLink = source.publicationLink || "";
  entityValues.editionNumber = source.editionNumber || 0;
  entityValues.price = Number(source.price) || 0;
  entityValues.showWorks =
    "works" in source && source.works
      ? (source.works || []).map((x) => {
          return { id: x.id, displayId: musicalWorkDisplayId(x, _langStore) };
        })
      : "showWorks" in source && source.showWorks
      ? (source.showWorks || []).map((x) => {
          return { id: x.id, displayId: x.displayId };
        })
      : [];
  entityValues.__works = entityValues.showWorks.map((x) => x.displayId);
  entityValues.notes = source.notes || "";
  entityValues.dndaExpedienteNumber = source.dndaExpedienteNumber || "";
  entityValues.contractObservation = source.contractObservation || "";
  entityValues.showDocumentation =
    "documentation" in source && source.documentation
      ? (source.documentation || []).map((x) => {
          const row: EditionDocumentationValuesType =
            getEditionDocumentationValues(x, _langStore, _duplicated);
          return { ...row, __status__: _duplicated ? "added" : "original" };
        })
      : "showDocumentation" in source && source.showDocumentation
      ? (source.showDocumentation || []).map((x) => {
          const row: EditionDocumentationValuesType =
            getEditionDocumentationValues(x, _langStore, _duplicated);
          // Fila de grilla ya existente: preservar su __status__ (una fila "added"
          // reseteada a "original" se clasifica en update[] con id temporal y sin
          // version_ — el server la rechaza). "original" solo como fallback para
          // filas sin status real (init "empty").
          return {
            ...row,
            __status__: _duplicated
              ? "added"
              : row.__status__ && row.__status__ !== "empty"
              ? row.__status__
              : "original",
          };
        })
      : [];
  entityValues.showAssociatedDocuments =
    "associatedDocuments" in source && source.associatedDocuments
      ? (source.associatedDocuments || []).map((x) => {
          const row: AssociatedDocumentEditionValuesType =
            getAssociatedDocumentEditionValues(x, _langStore, _duplicated);
          return { ...row, __status__: _duplicated ? "added" : "original" };
        })
      : "showAssociatedDocuments" in source && source.showAssociatedDocuments
      ? (source.showAssociatedDocuments || []).map((x) => {
          const row: AssociatedDocumentEditionValuesType =
            getAssociatedDocumentEditionValues(x, _langStore, _duplicated);
          // Fila de grilla ya existente: preservar su __status__ (una fila "added"
          // reseteada a "original" se clasifica en update[] con id temporal y sin
          // version_ — el server la rechaza). "original" solo como fallback para
          // filas sin status real (init "empty").
          return {
            ...row,
            __status__: _duplicated
              ? "added"
              : row.__status__ && row.__status__ !== "empty"
              ? row.__status__
              : "original",
          };
        })
      : [];
  entityValues.showUserAttachments =
    "userAttachments" in source && source.userAttachments
      ? (source.userAttachments || []).map((x) => {
          const row: UserAttachmentEditionValuesType =
            getUserAttachmentEditionValues(x, _langStore, _duplicated);
          return { ...row, __status__: _duplicated ? "added" : "original" };
        })
      : "showUserAttachments" in source && source.showUserAttachments
      ? (source.showUserAttachments || []).map((x) => {
          const row: UserAttachmentEditionValuesType =
            getUserAttachmentEditionValues(x, _langStore, _duplicated);
          // Fila de grilla ya existente: preservar su __status__ (una fila "added"
          // reseteada a "original" se clasifica en update[] con id temporal y sin
          // version_ — el server la rechaza). "original" solo como fallback para
          // filas sin status real (init "empty").
          return {
            ...row,
            __status__: _duplicated
              ? "added"
              : row.__status__ && row.__status__ !== "empty"
              ? row.__status__
              : "original",
          };
        })
      : [];
  entityValues.createdAt_ = source.createdAt_
    ? new Date(source.createdAt_)
    : null;
  entityValues.lastUpdatedAt_ = source.lastUpdatedAt_
    ? new Date(source.lastUpdatedAt_)
    : null;
  if (_duplicated) {
    entityValues.__status__ = "added";
  } else if ("__status__" in source) {
    entityValues.__status__ = source.__status__;
  }
  return entityValues;
}

export function getEditionUpdateVariables(
  values: EditionValuesType
): EditionUpdateMutationVariables {
  return {
    id: values.id,
    version_: values.version_,
    name: values.name,
    editionTypeId: values.showEditionType?.id || "",
    contentType: values.contentType,
    publicationLink: blankToNull(values.publicationLink),
    price: values.price,
    worksIds: values.showWorks
      ? { set: values.showWorks.map((x) => x.id) }
      : undefined,
    notes: blankToNull(values.notes),
    contractObservation: blankToNull(values.contractObservation),
    userAttachments: {
      create: values.showUserAttachments
        .filter((x) => addedStatusList.includes(x.__status__))
        .map((x) => getUserAttachmentEditionCreateInput(x)),
      update: values.showUserAttachments
        .filter((x) => originalUpdatedStatusList.includes(x.__status__))
        .map((x) => getUserAttachmentEditionUpdateInput(x)),
      remove: values.showUserAttachments
        .filter((x) => originalDeletedStatusList.includes(x.__status__))
        .map((x) => x.id),
    },
  };
}

export function getEditionCreateVariables(
  values: EditionValuesType
): EditionCreateMutationVariables {
  return {
    name: values.name,
    editionTypeId: values.showEditionType?.id || "",
    contentType: values.contentType,
    publicationLink: blankToNull(values.publicationLink),
    price: values.price,
    worksIds: values.showWorks.map((x) => x.id),
    notes: blankToNull(values.notes),
    contractObservation: blankToNull(values.contractObservation),
    userAttachments: values.showUserAttachments
      .filter((x) => addedStatusList.includes(x.__status__))
      .map((x) => getUserAttachmentEditionCreateInput(x)),
  };
}
